import { PreviewSheet } from '../../PreviewSheet';
import { ExternalLinkIcon } from '../../icons';
import type { Product } from '../types';
import { useTranslation } from '@hooks/useTranslation';
import { ProductStatusBadge } from './ProductStatusBadge';
import { ProductOverviewSection } from './ProductOverviewSection';
import { EquipmentSection } from './EquipmentSection';
import { StockSection } from './StockSection';
import { FooterEditLink } from '../../common/FooterEditLink';

interface ProductDetailSheetProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** RBAC: when false, the footer's Edit affordance is hidden. Defaults to false. */
  canWrite?: boolean;
  showBcrsDeposit?: boolean;
  showDisplayCopy?: boolean;
}

// -- Sub-components ----------------------------------------------------------

function SheetHeader({
  product,
  onClose,
}: {
  product: Product;
  onClose?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex w-full items-center justify-between">
      {/* Left: expand link */}
      <div className="flex items-center gap-3">
        <a
          href={`/products/${product.id}/details`}
          onClick={() => onClose?.()}
          className="text-grey hover:text-off-black transition-colors"
          aria-label={t('index.header.view_product_details')}
        >
          <ExternalLinkIcon />
        </a>
        <span className="font-inter text-lg font-semibold text-off-black">
          {t('index.header.view_product_details')}
        </span>
      </div>

      {/* Right: static status indicator */}
      <ProductStatusBadge status={product.status} />
    </div>
  );
}

// -- Main export -------------------------------------------------------------

export function ProductDetailSheet({
  product,
  open,
  onOpenChange,
  canWrite = false,
  showBcrsDeposit = false,
  showDisplayCopy = false,
}: ProductDetailSheetProps) {
  const { t } = useTranslation();
  return (
    <PreviewSheet
      open={open && product !== null}
      onOpenChange={onOpenChange}
      header={product ? <SheetHeader product={product} onClose={() => onOpenChange(false)} /> : undefined}
      footerClassName="justify-end"
      footer={product && canWrite ? <FooterEditLink href={`/products/${product.id}/edit`} label={t('common.footer.edit')} /> : undefined}
    >
      {product && (
        <div className="space-y-16">
          <ProductOverviewSection product={product} showBcrsDeposit={showBcrsDeposit} showDisplayCopy={showDisplayCopy} />
          <EquipmentSection product={product} />
          <StockSection product={product} />
        </div>
      )}
    </PreviewSheet>
  );
}
